import type { NoteBody } from '@backrs_ui/grow-ui/src/redux/coachingDashboard/coachingDashboard.types';

type Span = Readonly<{
  text: string;
  bold?: boolean;
}>;
type Paragraph = Readonly<{
  kind: 'p';
  spans: readonly Span[];
}>;
type BulletList = Readonly<{
  kind: 'ul';
  items: readonly (readonly Span[])[];
}>;
type Block = Paragraph | BulletList;

// Lexical text format bitmask: 1 = bold.
const FORMAT_BOLD = 1;

const p = (...spans: Span[]): Paragraph => ({ kind: 'p', spans });
const ul = (...items: Span[][]): BulletList => ({ kind: 'ul', items });

const textNode = ({ text, bold }: Span) => ({
  type: 'text',
  text,
  detail: 0,
  format: bold ? FORMAT_BOLD : 0,
  mode: 'normal',
  style: '',
  version: 1,
});

const paragraphNode = (spans: readonly Span[]) => ({
  type: 'paragraph',
  direction: 'ltr',
  format: '',
  indent: 0,
  version: 1,
  textFormat: 0,
  textStyle: '',
  children: spans.map(textNode),
});

const listItemNode = (spans: readonly Span[], value: number) => ({
  type: 'listitem',
  direction: 'ltr',
  format: '',
  indent: 0,
  version: 1,
  value,
  children: spans.map(textNode),
});

const listNode = (items: readonly (readonly Span[])[]) => ({
  type: 'list',
  direction: 'ltr',
  format: '',
  indent: 0,
  version: 1,
  listType: 'bullet',
  tag: 'ul',
  start: 1,
  children: items.map((spans, i) => listItemNode(spans, i + 1)),
});

const buildBody = (blocks: readonly Block[]): NoteBody => ({
  root: {
    type: 'root',
    direction: 'ltr',
    format: '',
    indent: 0,
    version: 1,
    children: blocks.map((block) =>
      block.kind === 'p' ? paragraphNode(block.spans) : listNode(block.items),
    ),
  },
});

export type CoachNoteTemplate = Readonly<{
  id: string;
  label: string;
  body: NoteBody;
}>;

export const COACH_NOTE_TEMPLATES: readonly CoachNoteTemplate[] = [
  {
    id: 'monthly-coaching-agenda',
    label: 'Monthly coaching agenda',
    body: buildBody([
      p({ text: 'Monthly Coaching Agenda', bold: true }),
      p(),
      p({ text: '1. Reflect - Start with a recent win', bold: true }),
      p(),
      p({ text: 'Surface a win', bold: true }),
      ul(
        [{ text: 'What’s something that went well recently that you feel proud of?' }],
        [
          {
            text: 'What’s a moment where you handled something better than you would have a year ago?',
          },
        ],
        [{ text: 'What’s a challenge you navigated well recently?' }],
      ),
      p(),
      p({ text: 'Unpack the success', bold: true }),
      ul(
        [{ text: 'What did you do that made that outcome possible?' }],
        [{ text: 'What strengths did you rely on?' }],
        [{ text: 'If we replayed that moment, what did you do well?' }],
      ),
      p(),
      p({ text: 'Extract the lesson', bold: true }),
      ul(
        [{ text: 'What does that success tell you about yourself?' }],
        [{ text: 'Where else could you apply that same approach?' }],
        [{ text: 'What lesson do you want to carry forward?' }],
      ),
      p(),
      p({ text: '2. Review - Check progress on previous commitments', bold: true }),
      p(),
      p({ text: 'Start the check-in', bold: true }),
      ul(
        [
          { text: 'Last time we talked about ' },
          { text: '[action step]', bold: true },
          { text: '. How did that go?' },
        ],
        [{ text: 'What progress did you make since our last conversation?' }],
      ),
      p(),
      p({ text: 'If progress happened', bold: true }),
      ul(
        [{ text: 'What helped you follow through?' }],
        [{ text: 'What part was easier than expected?' }],
        [{ text: 'What did you learn?' }],
      ),
      p(),
      p({ text: 'If progress stalled', bold: true }),
      ul(
        [{ text: 'What got in the way?' }],
        [{ text: 'Looking back, what might have helped?' }],
        [{ text: 'What would make this easier to execute moving forward?' }],
      ),
      p(),
      p({ text: '3. Diagnose - Surface the student’s current challenge', bold: true }),
      p(),
      p({ text: 'Open the floor', bold: true }),
      ul(
        [{ text: 'What feels most important for us to talk about today?' }],
        [{ text: 'What’s been most on your mind since we last met?' }],
        [{ text: 'If we could make progress on one thing today, what would it be?' }],
      ),
      p(),
      p({ text: 'Clarify the situation', bold: true }),
      ul(
        [{ text: 'What exactly is happening right now?' }],
        [{ text: 'What part feels most confusing?' }],
        [{ text: 'What decision are you trying to make?' }],
      ),
      p(),
      p({ text: 'Surface assumptions', bold: true }),
      ul(
        [{ text: 'What assumptions might you be making?' }],
        [{ text: 'What information might still be missing?' }],
      ),
      p(),
      p({ text: 'Find the constraint', bold: true }),
      ul(
        [{ text: 'What’s making this difficult right now?' }],
        [{ text: 'What do you think is the hardest part?' }],
      ),
      p(),
      p({ text: '4. Resolve - Help the student think through the challenge', bold: true }),
      p(),
      p({ text: 'Explore options', bold: true }),
      ul(
        [{ text: 'What options are you considering?' }],
        [{ text: 'What matters most to you in this decision?' }],
      ),
      p(),
      p({ text: 'Think through consequences', bold: true }),
      ul(
        [{ text: 'What might each option lead to over the next few years?' }],
        [{ text: 'What could be the upside?' }],
        [{ text: 'What risks or downsides should you consider?' }],
      ),
      p(),
      p({ text: 'Encourage perspective', bold: true }),
      ul(
        [{ text: 'If you were advising a friend, what would you say?' }],
        [{ text: 'Which option seems most aligned with the life you want to build?' }],
        [{ text: 'Which option would you regret not exploring?' }],
      ),
      p(),
      p({ text: '5. Focus - Identify the key lever', bold: true }),
      p({ text: 'Coach frames the recommendation.' }),
      p(
        {
          text: '“Based on where you are right now, the most important thing to focus on over the next ',
        },
        { text: '[month / quarter]', bold: true },
        { text: ' is ' },
        { text: '[priority]', bold: true },
        { text: '.”' },
      ),
      p(),
      p({ text: 'Check alignment', bold: true }),
      ul(
        [{ text: 'How does that focus area sound to you?' }],
        [{ text: 'Does that feel like the right place to concentrate your energy?' }],
        [{ text: 'What part of that feels most motivating to you?' }],
      ),
      p(),
      p({ text: '6. Follow Through - Lock in next steps', bold: true }),
      ul(
        [{ text: 'What is the most important step you’ll take before our next session?' }],
        [{ text: 'When ' }, { text: 'specifically' }, { text: ' will you do that?' }],
        [{ text: 'What might get in the way?' }],
      ),
      p(),
      p({ text: '7. Confirm:', bold: true }),
      ul(
        [{ text: 'clear action step' }],
        [{ text: 'timeframe' }],
        [{ text: 'relevant Backrs tools/resources' }],
        [{ text: 'lock in next month’s meeting ' }],
      ),
    ]),
  },
];
